import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Scale, Loader2, Plus, Trash2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Per-annexure client scoring scale: edit levels, paste text to extract, apply a saved scale, or follow the project default. */
export function DocScaleDialog({ project, doc, open, onOpenChange, onChanged }) {
  const [levels, setLevels] = useState([]);
  const [col, setCol] = useState('B');
  const [text, setText] = useState('');
  const [library, setLibrary] = useState([]);
  const [busy, setBusy] = useState(false);
  const own = !!doc?.scoring_scheme?.levels?.length;
  const fallback = project.scoring_scheme?.levels || [];
  useEffect(() => {
    if (!open || !doc) return;
    const s = own ? doc.scoring_scheme : (project.scoring_scheme || {});
    setLevels((s.levels || []).map(l => ({ ...l })));
    setCol(s.score_column || 'B');
    setText('');
    axios.get(`${API_URL}/api/rfp/response/scoring-scales`, { withCredentials: true }).then(r => setLibrary(r.data?.scales || [])).catch(() => {});
  }, [open, doc, own, project.scoring_scheme]);
  if (!doc) return null;
  const base = `${API_URL}/api/rfp/response/projects/${project.id}/documents/${doc.id}/scoring-scheme`;
  const save = async (body, msg) => {
    setBusy(true);
    try { await axios.put(base, body, { withCredentials: true }); toast.success(msg); onChanged(); onOpenChange(false); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the scale'); }
    finally { setBusy(false); }
  };
  const useDefault = async () => {
    setBusy(true);
    try { await axios.delete(base, { withCredentials: true }); toast.success('This annexure now follows the project default scale'); onChanged(); onOpenChange(false); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not reset'); }
    finally { setBusy(false); }
  };
  const setLv = (i, k, v) => setLevels(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rfp-doc-scale-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><Scale className="w-4 h-4 text-[#4ABFCF]" />Client scale — {doc.filename}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`text-[9px] ${own ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`} data-testid="rfp-doc-scale-source">
              {own ? `Own scale · ${doc.scoring_scheme.source || 'detected'}` : fallback.length ? 'Following the project default scale' : 'No scale yet'}
            </Badge>
            <span className="text-[10px] text-slate-500">Each annexure can carry the client's own scoring scale; rows, AI scoring, exports and the client workbook use it.</span>
          </div>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="rfp-doc-scale-levels">
            {levels.map((l, i) => (
              <div key={i} className="flex items-center gap-2 p-1.5">
                <Input value={l.label} onChange={e => setLv(i, 'label', e.target.value)} className="h-7 text-xs w-16" placeholder="3" data-testid={`rfp-doc-scale-label-${i}`} />
                <Input value={l.definition} onChange={e => setLv(i, 'definition', e.target.value)} className="h-7 text-xs flex-1" placeholder="Fully compliant, standard functionality" data-testid={`rfp-doc-scale-def-${i}`} />
                <button type="button" onClick={() => setLevels(ls => ls.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500" data-testid={`rfp-doc-scale-del-${i}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            <div className="p-1.5 flex items-center gap-2">
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setLevels(ls => [...ls, { label: '', definition: '' }])} data-testid="rfp-doc-scale-add"><Plus className="w-3 h-3 mr-1" />Add level</Button>
              <label className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-500">Score column
                <select value={col} onChange={e => setCol(e.target.value)} className="h-6 text-[10px] border border-slate-200 rounded px-1 bg-white" data-testid="rfp-doc-scale-col">
                  {'BCDEFGHIJ'.split('').map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" className="h-7 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" disabled={busy || levels.filter(l => l.label.trim()).length < 2}
              onClick={() => save({ levels: levels.filter(l => l.label.trim()), score_column: col }, 'Annexure scale saved')} data-testid="rfp-doc-scale-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save as this annexure's scale
            </Button>
            {library.length > 0 && (
              <select value="" onChange={e => { const s = library.find(x => x.id === e.target.value); if (s) setLevels(s.levels.map(l => ({ ...l }))); }} className="h-7 text-[11px] border border-slate-200 rounded-md px-1 bg-white text-slate-600" data-testid="rfp-doc-scale-library">
                <option value="">Load a saved scale…</option>{library.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            {own && <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" disabled={busy} onClick={useDefault} data-testid="rfp-doc-scale-use-default">Use project default</Button>}
          </div>
          <div className="space-y-1">
            <p className="text-[10px] text-slate-500">…or paste this annexure's scoring instructions and let AI extract the levels</p>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder={'e.g. 3 — Fully meets\n2 — Partially meets\n1 — Does not meet'} className="w-full text-xs border border-slate-200 rounded-md p-2" data-testid="rfp-doc-scale-text" />
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || text.trim().length < 10} onClick={() => save({ text, score_column: col }, 'Scale extracted and saved for this annexure')} data-testid="rfp-doc-scale-extract">
              <Sparkles className="w-3.5 h-3.5 mr-1" />Extract & save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
